import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Tag } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, ExternalLink, Copy, Pencil, ImagePlus, Loader2, FileText, FolderOpen, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import usePermissions from "@/lib/usePermissions";
import { CandidateDocumentViewer } from "@/components/CandidateDocumentViewer";
import DriveLinkPreviewModal from "@/components/DriveLinkPreviewModal";
import { CandidateDetailModal } from "@/components/CandidateDetailModal";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";

const STAGES = ["applied", "screening", "interview", "offer", "hired", "rejected"];
const NOTICE_PERIOD_OPTIONS = ["Immediate", "15 Days", "30 Days", "60 Days", "90 Days"];

function firstTwoSentences(text) {
  if (!text) return "";
  const sentences = text.match(/[^.!?]+[.!?]+|[^.!?]+$/g) || [text];
  return sentences.slice(0, 2).join(" ").trim();
}

function DriveLinkField({ label, kind, form, onChange }) {
  const value = form[`${kind}_path`] || "";
  return (
    <div>
      <Label className="label-overline">{label} — Drive link</Label>
      <Input className="rounded-sm mt-1" placeholder="https://drive.google.com/…" value={value} onChange={(e) => onChange(kind, e.target.value)} data-testid={`cand-${kind}-link`} />
    </div>
  );
}
const TONE = { applied: "slate", screening: "blue", interview: "yellow", offer: "orange", hired: "green", rejected: "red" };
const BUS = ["Strategy", "BU1", "BU2", "BU3", "Operations"];
const ROLE_PRESETS = [
  "Senior I.D.",
  "Senior Mechanical Engineer",
  "Mechanical Engineering Intern",
  "I.D. Intern",
  "Industrial Designer",
  "Industrial Design Intern",
  "Electronics Engineer",
  "Freelance Electronics Designer",
  "Freelance Mechanical Engineer",
  "Mechanical Engineer",
  "GTM Specialist",
  "Founder's Office Generalist",
  "Venture Strategist",
  "Funding Lead",
  "Sales Design Strategist",
  "HR Admin Manager",
  "Operations Manager",
  "Client Manager",
  "Project Manager",
];

export default function Recruitment() {
  const { user } = useAuth();
  const can = usePermissions();
  const canCreateReq = can("recruitment_create");
  const canEditReq = can("recruitment_edit");
  const canEdit = canCreateReq || canEditReq;
  const [tab, setTab] = useState("pipeline");
  const [candidates, setCandidates] = useState([]);
  const [roles, setRoles] = useState([]);
  const [candOpen, setCandOpen] = useState(false);
  const [roleOpen, setRoleOpen] = useState(false);
  const [candForm, setCandForm] = useState({
    name: "", email: "", role_applied: ROLE_PRESETS[0], role_id: "",
    stage: "applied", business_unit: "BU1", notes: "",
    years_experience: "", current_salary: "", expected_salary: "", notice_period: "",
    resume_path: "", portfolio_path: "", project_path: "",
  });
  const EMPTY_ROLE = { title: "", description: "", role_overview: "", responsibilities: "", requirements: "", qualifications: "", cover_image_url: "", business_unit: "BU1", location: "Bangalore", employment_type: "Full-time", is_open: true };
  const [roleForm, setRoleForm] = useState(EMPTY_ROLE);
  const [editingRoleId, setEditingRoleId] = useState(null);
  const [coverUploading, setCoverUploading] = useState(false);
  const [viewerDoc, setViewerDoc] = useState(null); // { path, title } — legacy object-storage doc (public Careers apply)
  const [drivePreview, setDrivePreview] = useState(null); // { url, title } — Drive-link doc (internal Add candidate)
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const confirmDeleteCandidate = async () => {
    if (!deleteCandidate) return;
    setDeleting(true);
    try {
      await api.delete(`/candidates/${deleteCandidate.id}`);
      toast.success("Candidate deleted");
      setDeleteCandidate(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDeleting(false); }
  };

  const careersUrl = `${window.location.origin}/careers`;
  const openRoles = useMemo(() => roles.filter((r) => r.is_open), [roles]);

  const load = async () => {
    const [c, r] = await Promise.all([api.get("/candidates"), api.get("/roles")]);
    setCandidates(c.data); setRoles(r.data);
  };
  useEffect(() => { load(); }, []);

  const createCand = async () => {
    if (candForm.current_salary === "") { toast.error("Current salary is required"); return; }
    if (!candForm.notice_period) { toast.error("Notice period is required"); return; }
    try {
      const payload = {
        ...candForm,
        role_id: candForm.role_id || null,
        years_experience: candForm.years_experience === "" ? null : Number(candForm.years_experience),
        current_salary: candForm.current_salary === "" ? null : Number(candForm.current_salary),
        expected_salary: candForm.expected_salary === "" ? null : Number(candForm.expected_salary),
      };
      await api.post("/candidates", payload);
      toast.success("Candidate added");
      setCandOpen(false);
      setCandForm({ name: "", email: "", role_applied: ROLE_PRESETS[0], role_id: "", stage: "applied", business_unit: "BU1", notes: "", years_experience: "", current_salary: "", expected_salary: "", notice_period: "", resume_path: "", portfolio_path: "", project_path: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setCandDriveLink = (kind, value) => setCandForm((f) => ({ ...f, [`${kind}_path`]: value }));
  const setStage = async (id, stage) => {
    try { await api.patch(`/candidates/${id}`, { stage }); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const createRole = async () => {
    if (!roleForm.title.trim()) { toast.error("Title is required"); return; }
    try {
      if (editingRoleId) {
        await api.patch(`/roles/${editingRoleId}`, roleForm);
        toast.success("Role updated");
      } else {
        await api.post("/roles", roleForm);
        toast.success("Role posted");
      }
      setRoleOpen(false); setEditingRoleId(null); setRoleForm(EMPTY_ROLE); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const startEditRole = (r) => {
    setEditingRoleId(r.id);
    setRoleForm({ ...EMPTY_ROLE, ...r });
    setRoleOpen(true);
  };
  const uploadCover = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    e.target.value = "";
    if (!file.type.startsWith("image/")) { toast.error("Pick a JPG / PNG image"); return; }
    setCoverUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("folder", "roles");
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = data.path.startsWith("/api/files/") ? `${process.env.REACT_APP_BACKEND_URL}${data.path}` : data.path;
      setRoleForm((f) => ({ ...f, cover_image_url: url }));
      toast.success("Cover image uploaded");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setCoverUploading(false); }
  };
  const toggleRole = async (r) => {
    try { await api.patch(`/roles/${r.id}`, { is_open: !r.is_open }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const byStage = useMemo(() => {
    const m = Object.fromEntries(STAGES.map((s) => [s, []]));
    for (const r of candidates) if (m[r.stage]) m[r.stage].push(r);
    return m;
  }, [candidates]);

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto fade-in" data-testid="recruitment-page">
      <PageHeader
        eyebrow="People"
        title="Recruitment"
        subtitle="Manage open roles + applicant pipeline. Public careers page lives at /careers — share it from analogydesign.co."
        actions={canEdit && (
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-sm" onClick={() => { navigator.clipboard.writeText(careersUrl); toast.success("Careers URL copied"); }} data-testid="copy-careers">
              <Copy size={14} className="mr-1.5" />Copy careers link
            </Button>
            <a href="/careers" target="_blank" rel="noreferrer">
              <Button variant="outline" className="rounded-sm"><ExternalLink size={14} className="mr-1.5" />Open careers</Button>
            </a>
          </div>
        )}
      />

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="rounded-sm">
          <TabsTrigger value="pipeline" className="rounded-sm" data-testid="tab-pipeline">Pipeline</TabsTrigger>
          <TabsTrigger value="roles" className="rounded-sm" data-testid="tab-roles">Open roles ({roles.filter((r) => r.is_open).length})</TabsTrigger>
        </TabsList>

        <TabsContent value="pipeline" className="mt-4">
          {canCreateReq && (
            <div className="flex justify-end mb-3">
              <Dialog open={candOpen} onOpenChange={setCandOpen}>
                <DialogTrigger asChild><Button className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="new-candidate-btn"><Plus size={14} className="mr-1.5" />Add candidate</Button></DialogTrigger>
                <DialogContent className="rounded-sm max-w-2xl">
                  <DialogHeader><DialogTitle className="font-display">Add candidate</DialogTitle></DialogHeader>
                  <div className="space-y-3 mt-2 max-h-[65vh] overflow-y-auto pr-1">
                    <div className="grid grid-cols-2 gap-3">
                      <Input className="rounded-sm" placeholder="Full name" value={candForm.name} onChange={(e) => setCandForm({ ...candForm, name: e.target.value })} data-testid="cand-name" />
                      <Input className="rounded-sm" placeholder="Email" value={candForm.email} onChange={(e) => setCandForm({ ...candForm, email: e.target.value })} data-testid="cand-email" />
                    </div>
                    <div>
                      <Label className="label-overline">Role applied for</Label>
                      <Select value={candForm.role_applied} onValueChange={(v) => setCandForm({ ...candForm, role_applied: v })}>
                        <SelectTrigger className="rounded-sm mt-1" data-testid="cand-role"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {ROLE_PRESETS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    {openRoles.length > 0 && (
                      <div>
                        <Label className="label-overline">Link to open role (optional)</Label>
                        <Select value={candForm.role_id || "none"} onValueChange={(v) => setCandForm({ ...candForm, role_id: v === "none" ? "" : v })}>
                          <SelectTrigger className="rounded-sm mt-1" data-testid="cand-role-id"><SelectValue placeholder="—" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">— (none)</SelectItem>
                            {openRoles.map((r) => <SelectItem key={r.id} value={r.id}>{r.title} · {r.business_unit}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <Label className="label-overline">Years exp.</Label>
                        <Input type="number" step="0.5" className="rounded-sm mt-1" value={candForm.years_experience} onChange={(e) => setCandForm({ ...candForm, years_experience: e.target.value })} data-testid="cand-yoe" />
                      </div>
                      <div>
                        <Label className="label-overline">Current salary (₹) *</Label>
                        <Input required type="number" className="rounded-sm mt-1" value={candForm.current_salary} onChange={(e) => setCandForm({ ...candForm, current_salary: e.target.value })} data-testid="cand-cur-sal" />
                      </div>
                      <div>
                        <Label className="label-overline">Expected (₹)</Label>
                        <Input type="number" className="rounded-sm mt-1" value={candForm.expected_salary} onChange={(e) => setCandForm({ ...candForm, expected_salary: e.target.value })} data-testid="cand-exp-sal" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label className="label-overline">Business unit</Label>
                        <Select value={candForm.business_unit} onValueChange={(v) => setCandForm({ ...candForm, business_unit: v })}>
                          <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                          <SelectContent>{BUS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="label-overline">Notice period *</Label>
                        <Select value={candForm.notice_period} onValueChange={(v) => setCandForm({ ...candForm, notice_period: v })}>
                          <SelectTrigger className="rounded-sm mt-1" data-testid="cand-notice-period"><SelectValue placeholder="Select…" /></SelectTrigger>
                          <SelectContent>{NOTICE_PERIOD_OPTIONS.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <DriveLinkField label="Resume" kind="resume" form={candForm} onChange={setCandDriveLink} />
                      <DriveLinkField label="Portfolio" kind="portfolio" form={candForm} onChange={setCandDriveLink} />
                      <DriveLinkField label="Project sample" kind="project" form={candForm} onChange={setCandDriveLink} />
                    </div>
                    <Textarea rows={3} className="rounded-sm" placeholder="Notes" value={candForm.notes} onChange={(e) => setCandForm({ ...candForm, notes: e.target.value })} />
                  </div>
                  <DialogFooter><Button onClick={createCand} className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white" data-testid="cand-save">Add</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {STAGES.map((s) => (
              <div key={s} className="card-flat p-3 min-h-[320px]">
                <div className="flex items-center justify-between mb-3">
                  <Tag tone={TONE[s]}>{s}</Tag>
                  <span className="text-xs text-slate-400">{byStage[s].length}</span>
                </div>
                <div className="space-y-2">
                  {byStage[s].map((r) => (
                    <div
                      key={r.id}
                      className="bg-white border border-slate-200 rounded-sm p-3 hover:border-[#F54800]/50 hover:shadow-sm transition-all cursor-pointer"
                      data-testid={`candidate-${r.id}`}
                      onClick={() => setSelectedCandidate(r)}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-slate-900 truncate">{r.name}</div>
                          <div className="text-xs text-slate-500 truncate">{r.role_applied} · {r.business_unit || "—"}</div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                          {r.culture_fit?.overall_score > 0 && (
                            <span className="text-[10px] font-bold text-[#F54800] bg-orange-50 rounded-full px-1.5 py-0.5 inline-flex items-center gap-0.5" data-testid={`cf-score-badge-${r.id}`}>
                              ★ {r.culture_fit.overall_score}
                            </span>
                          )}
                          {user?.role === "founder" && (
                            <button
                              onClick={() => setDeleteCandidate(r)}
                              className="text-slate-300 hover:text-red-600 p-0.5"
                              title="Delete candidate"
                              data-testid={`delete-candidate-${r.id}`}
                            >
                              <Trash2 size={13} />
                            </button>
                          )}
                        </div>
                      </div>
                      {r.source === "careers_page" && <div className="text-[10px] uppercase tracking-wider text-[#F54800] mt-1 font-semibold">via careers</div>}
                      {r.notes && (
                        <p className="text-[11px] text-slate-500 mt-2 line-clamp-2" data-testid={`cover-note-preview-${r.id}`}>{firstTwoSentences(r.notes)}</p>
                      )}
                      {(r.current_salary != null || r.notice_period) && (
                        <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                          {r.current_salary != null && (
                            <span className="text-[10px] bg-slate-100 text-slate-600 rounded-sm px-1.5 py-0.5" data-testid={`cand-salary-badge-${r.id}`}>₹{Number(r.current_salary).toLocaleString("en-IN")}</span>
                          )}
                          {r.notice_period && (
                            <span className="text-[10px] bg-slate-100 text-slate-600 rounded-sm px-1.5 py-0.5" data-testid={`cand-notice-badge-${r.id}`}>{r.notice_period}</span>
                          )}
                        </div>
                      )}
                      <div className="flex items-center gap-3 mt-2" onClick={(e) => e.stopPropagation()}>
                        {r.resume_path && (
                          <button onClick={() => r.resume_path.startsWith("http") ? setDrivePreview({ url: r.resume_path, title: `${r.name} — Resume` }) : setViewerDoc({ path: r.resume_path, title: `${r.name} — Resume` })} className="text-[10px] text-slate-500 hover:text-[#F54800] inline-flex items-center gap-1" data-testid={`resume-${r.id}`}>
                            <FileText size={11} /> Resume
                          </button>
                        )}
                        {r.portfolio_path && (
                          <button onClick={() => r.portfolio_path.startsWith("http") ? setDrivePreview({ url: r.portfolio_path, title: `${r.name} — Portfolio` }) : setViewerDoc({ path: r.portfolio_path, title: `${r.name} — Portfolio` })} className="text-[10px] text-slate-500 hover:text-[#F54800] inline-flex items-center gap-1" data-testid={`portfolio-${r.id}`}>
                            <FolderOpen size={11} /> Portfolio
                          </button>
                        )}
                      </div>
                      {canEditReq && (
                        <div onClick={(e) => e.stopPropagation()}>
                          <Select value={r.stage} onValueChange={(v) => setStage(r.id, v)}>
                            <SelectTrigger className="rounded-sm h-7 mt-2 text-xs" data-testid={`stage-select-${r.id}`}><SelectValue /></SelectTrigger>
                            <SelectContent>{STAGES.map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="roles" className="mt-4">
          {canCreateReq && (
            <div className="flex justify-end mb-3">
              <Dialog open={roleOpen} onOpenChange={(v) => { setRoleOpen(v); if (!v) { setEditingRoleId(null); setRoleForm(EMPTY_ROLE); } }}>
                <DialogTrigger asChild><Button className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="new-role-btn"><Plus size={14} className="mr-1.5" />Post a role</Button></DialogTrigger>
                <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader><DialogTitle className="font-display">{editingRoleId ? "Edit role" : "Post an open role"}</DialogTitle></DialogHeader>
                  <p className="text-xs text-slate-500">Roles appear at <span className="font-mono text-[#F54800]">/careers</span> — share that link.</p>
                  <div className="space-y-3 mt-2">
                    <Input className="rounded-sm" placeholder="Role title" value={roleForm.title} onChange={(e) => setRoleForm({ ...roleForm, title: e.target.value })} data-testid="role-title" />
                    {/* Cover image */}
                    <div>
                      <Label className="label-overline">Cover image (JPG/PNG · 16:9 cropped)</Label>
                      {roleForm.cover_image_url && (
                        <div className="mt-2 relative aspect-video rounded-sm overflow-hidden border border-slate-200 bg-slate-50">
                          <img src={roleForm.cover_image_url} alt="cover" className="absolute inset-0 w-full h-full object-cover" />
                          <button type="button" onClick={() => setRoleForm({ ...roleForm, cover_image_url: "" })} className="absolute top-1 right-1 bg-white/90 rounded-sm text-[10px] px-2 py-0.5 text-red-600 hover:bg-white" data-testid="role-cover-clear">Remove</button>
                        </div>
                      )}
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        <label className={`flex items-center justify-center gap-1 h-9 border rounded-sm text-xs cursor-pointer ${roleForm.cover_image_url ? "border-green-300 bg-green-50 text-green-700" : "border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100"}`} data-testid="role-cover-upload">
                          {coverUploading ? <Loader2 size={12} className="animate-spin" /> : <ImagePlus size={12} />}
                          {coverUploading ? "Uploading…" : "Upload image"}
                          <input type="file" accept="image/jpeg,image/png" className="hidden" onChange={uploadCover} />
                        </label>
                        <Input className="rounded-sm h-9 text-xs" placeholder="…or paste image / Drive URL" value={roleForm.cover_image_url || ""} onChange={(e) => setRoleForm({ ...roleForm, cover_image_url: e.target.value })} data-testid="role-cover-url" />
                      </div>
                    </div>
                    <Textarea rows={3} className="rounded-sm" placeholder="Role overview (1-2 lines, shown at the top)" value={roleForm.role_overview || ""} onChange={(e) => setRoleForm({ ...roleForm, role_overview: e.target.value })} data-testid="role-overview" />
                    <Textarea rows={3} className="rounded-sm" placeholder="Full job description (markdown OK)" value={roleForm.description} onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })} />
                    <Textarea rows={3} className="rounded-sm" placeholder="Responsibilities (one per line)" value={roleForm.responsibilities || ""} onChange={(e) => setRoleForm({ ...roleForm, responsibilities: e.target.value })} data-testid="role-responsibilities" />
                    <Textarea rows={3} className="rounded-sm" placeholder="Requirements (one per line)" value={roleForm.requirements || ""} onChange={(e) => setRoleForm({ ...roleForm, requirements: e.target.value })} data-testid="role-requirements" />
                    <Textarea rows={2} className="rounded-sm" placeholder="Qualifications" value={roleForm.qualifications || ""} onChange={(e) => setRoleForm({ ...roleForm, qualifications: e.target.value })} data-testid="role-qualifications" />
                    <div className="grid grid-cols-3 gap-3">
                      <Select value={roleForm.business_unit} onValueChange={(v) => setRoleForm({ ...roleForm, business_unit: v })}>
                        <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>{BUS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                      </Select>
                      <Input className="rounded-sm" placeholder="Location" value={roleForm.location} onChange={(e) => setRoleForm({ ...roleForm, location: e.target.value })} />
                      <Select value={roleForm.employment_type} onValueChange={(v) => setRoleForm({ ...roleForm, employment_type: v })}>
                        <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Full-time">Full-time</SelectItem>
                          <SelectItem value="Part-time">Part-time</SelectItem>
                          <SelectItem value="Internship">Internship</SelectItem>
                          <SelectItem value="Contract">Contract</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <DialogFooter><Button onClick={createRole} className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white" data-testid="save-role">{editingRoleId ? "Save changes" : "Publish"}</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          )}
          <div className="card-flat">
            <div className="grid grid-cols-5 gap-4 px-5 py-3 border-b border-slate-200 label-overline">
              <div className="col-span-2">Role</div><div>BU</div><div>Type</div><div className="text-right">Status</div>
            </div>
            <div className="divide-row">
              {roles.map((r) => {
                const days = r.end_date ? Math.ceil((new Date(r.end_date) - new Date()) / (1000 * 60 * 60 * 24)) : null;
                return (
                <div key={r.id} className="grid grid-cols-5 gap-4 px-5 py-3 text-sm items-center hover-row" data-testid={`role-row-${r.id}`}>
                  <div className="col-span-2">
                    <div className="text-slate-900 font-medium">{r.title}</div>
                    <div className="text-xs text-slate-500 line-clamp-1">{r.description}</div>
                    {r.is_open && days !== null && (() => {
                      let color = "#64748b";
                      if (days <= 3) color = "#dc2626";
                      else if (days <= 7) color = "#F54800";
                      let label = `${days} days left`;
                      if (days === 0) label = "Closes today";
                      else if (days < 0) label = "Past close date";
                      return (
                        <div className="text-[10px] uppercase tracking-wider font-semibold mt-1" style={{ color }}>
                          {label}
                        </div>
                      );
                    })()}
                  </div>
                  <div>{r.business_unit || "—"}</div>
                  <div>{r.employment_type}</div>
                  <div className="text-right flex items-center justify-end gap-2">
                    <Tag tone={r.is_open ? "green" : "slate"}>{r.is_open ? "open" : "closed"}</Tag>
                    {canEditReq && (
                      <>
                        <input type="date" defaultValue={r.end_date || ""} onBlur={(e) => api.patch(`/roles/${r.id}/dates`, { end_date: e.target.value }).then(load)} className="text-xs border border-slate-200 rounded-sm px-1 h-7" data-testid={`role-end-${r.id}`} />
                        <Button size="sm" variant="outline" className="rounded-sm h-7 px-2" onClick={() => startEditRole(r)} data-testid={`edit-role-${r.id}`}><Pencil size={11} /></Button>
                        <Button size="sm" variant="outline" className="rounded-sm h-7" onClick={() => toggleRole(r)} data-testid={`toggle-role-${r.id}`}>{r.is_open ? "Close" : "Open"}</Button>
                      </>
                    )}
                  </div>
                </div>
                );
              })}
              {roles.length === 0 && <div className="p-5 text-sm text-slate-500">No roles posted yet.</div>}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <CandidateDocumentViewer
        open={!!viewerDoc}
        path={viewerDoc?.path}
        title={viewerDoc?.title}
        onClose={() => setViewerDoc(null)}
      />
      <DriveLinkPreviewModal open={!!drivePreview} url={drivePreview?.url} title={drivePreview?.title} onClose={() => setDrivePreview(null)} />
      <CandidateDetailModal
        candidate={selectedCandidate}
        open={!!selectedCandidate}
        onClose={() => setSelectedCandidate(null)}
        onSaved={load}
      />
      <AlertDialog open={!!deleteCandidate} onOpenChange={(o) => { if (!deleting && !o) setDeleteCandidate(null); }}>
        <AlertDialogContent className="rounded-sm" data-testid="delete-candidate-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this candidate?</AlertDialogTitle>
            <AlertDialogDescription>
              Permanently delete <b>{deleteCandidate?.name}</b>&apos;s application, resume, portfolio and Culture Fit evaluation. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="delete-candidate-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmDeleteCandidate(); }}
              disabled={deleting}
              className="rounded-sm bg-red-600 hover:bg-red-700 text-white"
              data-testid="delete-candidate-confirm-btn"
            >
              {deleting ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
